'use client'
import type React from 'react'
import { forwardRef } from 'react'
import { StepState } from '../step'

export interface StepProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Title */
  label: string
  /** Step description */
  description: string
  /** State of Step */
  state?: StepState
  /** Label or Button */
  labelOnly?: boolean
  /** Step number */
  stepNumber?: number

  /** Size of Arrow Step  */
  size?: {
    height?: number
    heightWithDesc?: number
    leftCut?: number
    rightCut?: number
    rounded?: number
    gapX?: number
  }

  /** Color of Element in Arrow Step */
  colors?: {
    currentBg?: string
    currentText?: string
    currentDescText?: string
    doneBg?: string
    doneText?: string
    doneDescText?: string
    todoBg?: string
    todoText?: string
    todoDescText?: string
    separatorShadow?: string
    descBg?: string
  }
}

const cx = (...args: (string | false | null | undefined)[]) => args.filter(Boolean).join(' ')

/**
 * ArrowStep
 */
export const Step = forwardRef<HTMLDivElement, StepProps>(
  (
    {
      label,
      description,
      state = StepState.CURRENT,
      labelOnly = true,
      stepNumber = 1,
      size = {
        height: 44,
        heightWithDesc: 56,
        leftCut: 14,
        rightCut: 14,
        rounded: 0,
        gapX: 16,
      },
      colors = {
        currentBg: 'bg-orange-600',
        currentText: 'text-white',
        currentDescText: 'text-orange-50',

        doneBg: 'bg-blue-500',
        doneText: 'text-white',
        doneDescText: 'text-white',

        todoBg: 'bg-gray-300',
        todoText: 'text-gray-900',
        todoDescText: 'text-gray-700',

        separatorShadow: 'shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]',
        descBg: 'bg-orange-500',
      },
      className,
      onClick,
      onKeyDown,
      tabIndex,
      role,
      ...rest
    },
    ref
  ) => {
    const H = size.heightWithDesc ?? 56
    const LEFT = size.leftCut ?? 14
    const RIGHT = size.rightCut ?? 14
    const R = size.rounded ?? 8
    const PADX = size.gapX ?? 16
    const MARGIN_RIGHT = (stepNumber - 1) * RIGHT

    const bgClass =
      state === StepState.CURRENT
        ? colors.currentBg!
        : state === StepState.DONE
          ? colors.doneBg!
          : colors.todoBg!

    const textClass =
      state === StepState.CURRENT
        ? colors.currentText!
        : state === StepState.DONE
          ? colors.doneText!
          : colors.todoText!

    const isInteractive = !labelOnly && typeof onClick === 'function'

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
      onKeyDown?.(e)
      if (!isInteractive) return
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick?.(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>)
      }
    }

    return (
      <div
        ref={ref}
        role={role ?? (isInteractive ? 'button' : undefined)}
        tabIndex={tabIndex ?? (isInteractive ? 0 : undefined)}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        className={cx(
          'inline-block focus:outline-none',
          isInteractive &&
            'focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-0',
          className
        )}
        {...rest}
      >
        <div
          className={cx(
            'relative inline-flex select-none flex-col items-center justify-center font-semibold',
            colors.separatorShadow,
            bgClass,
            textClass,
            isInteractive ? 'cursor-pointer' : 'cursor-default'
          )}
          style={{
            height: H,
            paddingLeft: PADX,
            paddingRight: PADX,
            borderRadius: R,
            right: MARGIN_RIGHT,
            overflow: 'visible',
            clipPath: `polygon(0 0, calc(100% - ${LEFT}px) 0, 100% 50%, calc(100% - ${RIGHT}px) 100%, 0 100%, ${LEFT}px 50%)`,
          }}
        >
          <span className="text-[13px] uppercase tracking-wide sm:text-[10px]">{label}</span>

          <span className="mt-1 truncate rounded-md px-2.5 py-1 font-semibold text-[13px] sm:text-[11px]">
            {description}
          </span>
        </div>
      </div>
    )
  }
)

Step.displayName = 'ArrowStepDoubleNotch'
